import type { ComparablesDataEntry, MarketComparison, MarketComparisonItem } from '@einwert/valuation-api-client'
import { Box, Stack, Tab, Tabs } from '@mui/material'

import { ComparisonChart, ContentChip, InfoTooltip, InputChip, ReasonIcon, tokens, Typography } from 'ComponentLibrary'
import {
    getDataEntryTabsByMarketType,
    getMarketComparisonTitle,
    getMarketRatingChipProps,
    getUnitByDataType,
} from 'ClientPortal/src/pages/Assets/AssetDetails/Valuation/ValuationMarketData/Comparables/constants'
import {
    ComparableDataWithIndex,
    filterMarketDataByDataEntryType,
    mapMarketDataToComparisonSource,
} from 'ClientPortal/src/pages/Assets/AssetDetails/Valuation/ValuationMarketData/Comparables/market-data'
import { isEmpty } from 'lodash'
import { useState } from 'react'
import {
    getValueReference,
    ValueReferences,
} from 'ClientPortal/src/pages/Assets/AssetDetails/Valuation/ValuationMarketData/Comparables/value-reference'
import { ReferencePicker } from 'ClientPortal/src/pages/Assets/AssetDetails/Valuation/ValuationMarketData/Comparables/ReferencePicker'
import { useTranslation } from 'react-i18next'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'

type ComparablesCategoryProps = {
    marketData: ComparableDataWithIndex[]
    marketComparisonItem?: MarketComparisonItem
    valueReference: ValueReferences
    marketDataType: keyof MarketComparison
    marketAreaDefinition?: string
    marketAreaReferenceUnit?: string
    valuationDate: string | undefined
}

export const ComparablesCategory = ({
    marketData,
    marketComparisonItem,
    marketDataType,
    valueReference,
    valuationDate,
    marketAreaDefinition,
    marketAreaReferenceUnit,
}: ComparablesCategoryProps) => {
    const { t: tCommon } = useTranslation('terminology', { keyPrefix: 'common' })
    const { t: tMarketData } = useTranslation('terminology', { keyPrefix: 'marketData' })
    const { t: tComparablesCategory } = useTranslation('pages', {
        keyPrefix: 'assets.assetDetails.valuation.valuationMarketData.comparablesCategory',
    })
    const locale = useCurrentResolvedLocale()
    const tabs = getDataEntryTabsByMarketType(marketData, marketDataType, tComparablesCategory)
    const firstAvailableDataEntryType = tabs.at(0)?.value
    const [selectedDataEntryType, setSelectedDataEntryType] = useState<ComparablesDataEntry['data_type'] | undefined>(
        firstAvailableDataEntryType
    )
    const [selectedYieldReference, setSelectedYieldReference] = useState<'gross_initial_yield' | 'gross_yield'>('gross_yield')
    const [selectedMultiplierReference, setSelectedMultiplierReference] = useState<'gross_yield_multiplier' | 'net_yield_multiplier'>(
        'gross_yield_multiplier'
    )
    const activeDataEntryType = tabs.some(({ value }) => value === selectedDataEntryType)
        ? selectedDataEntryType
        : firstAvailableDataEntryType
    const comparisonChartCopy = {
        emptyLabel: tComparablesCategory('chart.emptyLabel'),
        generatedValuesTooltip: tComparablesCategory('chart.generatedValuesTooltip'),
        sourceDateWarning: tComparablesCategory('chart.sourceDateWarning'),
        relevantDataPoints: (count: number) => tComparablesCategory('chart.relevantDataPoints', { count }),
        totalDataPoints: (count: number) => tComparablesCategory('chart.totalDataPoints', { count }),
        unknownDataPointCount: tComparablesCategory('chart.unknownDataPointCount'),
        unnamedDataSeries: tComparablesCategory('chart.unnamedDataSeries'),
    }

    if (isEmpty(marketData) || !activeDataEntryType) {
        return null
    }

    const selectedMarketData = filterMarketDataByDataEntryType(marketData, activeDataEntryType)
    const reasons = marketComparisonItem?.reasons ?? []
    const marketComparisonRating = marketComparisonItem?.market_comparison_rating
    const selectedReference = getValueReference(activeDataEntryType, valueReference, selectedYieldReference, selectedMultiplierReference)

    return (
        <Stack
            direction="column"
            gap={tokens.SizingSpacing400}
            padding={tokens.SizingSpacing200}
            borderRadius={tokens.SizingCornerL}
            border={`1px solid ${tokens.ColorBorderDefault}`}
            bgcolor={tokens.ColorBackgroundLight}
        >
            <Stack direction="column" gap={tokens.SizingSpacing200}>
                <Stack direction="row" justifyContent="space-between" height="24px">
                    <Stack direction="row" gap={tokens.SizingSpacing200}>
                        <Typography variant="headline3">
                            {marketDataType === 'market_value'
                                ? tCommon('prices')
                                : getMarketComparisonTitle(marketDataType, tComparablesCategory)}
                        </Typography>
                        {marketComparisonRating && (
                            <ContentChip {...getMarketRatingChipProps(marketComparisonRating, tMarketData)} size="small" />
                        )}
                    </Stack>
                    {activeDataEntryType === 'market_area_value' && marketAreaDefinition && (
                        <InfoTooltip text={tComparablesCategory('marketAreaValueTooltip', { value: marketAreaDefinition })} />
                    )}
                </Stack>
                <Stack direction="row" gap={tokens.SizingSpacing100} flexWrap="wrap">
                    {reasons.map((reason) => (
                        <InputChip key={reason.label} label={reason.label} icon={<ReasonIcon type={reason.type} />} />
                    ))}
                </Stack>
            </Stack>
            {tabs.length > 1 && (
                <Tabs
                    variant="fullWidth"
                    sx={{ minHeight: 0 }}
                    value={activeDataEntryType}
                    onChange={(_, newValue) => setSelectedDataEntryType(newValue)}
                >
                    {tabs.map((type) => (
                        <Tab key={type.value} value={type.value} label={type.label.toUpperCase()} />
                    ))}
                </Tabs>
            )}
            <Box marginTop={tokens.SizingSpacing100}>
                <ComparisonChart
                    key={activeDataEntryType}
                    sources={mapMarketDataToComparisonSource(selectedMarketData, valuationDate, tComparablesCategory, locale)}
                    unit={getUnitByDataType(activeDataEntryType, tComparablesCategory)}
                    reference={selectedReference}
                    referenceUnit={activeDataEntryType === 'market_area_value' ? marketAreaReferenceUnit : undefined}
                    copy={comparisonChartCopy}
                    locale={locale}
                />
            </Box>
            {activeDataEntryType === 'yield' && (
                <ReferencePicker
                    label={tComparablesCategory('referencePickerLabel')}
                    options={[
                        { value: 'gross_yield', label: tComparablesCategory('yieldOptions.grossYield') },
                        { value: 'gross_initial_yield', label: tComparablesCategory('yieldOptions.grossInitialYield') },
                    ]}
                    setSelection={setSelectedYieldReference}
                    selection={selectedYieldReference}
                />
            )}
            {activeDataEntryType === 'multiplier' && (
                <ReferencePicker
                    label={tComparablesCategory('referencePickerLabel')}
                    options={[
                        {
                            value: 'gross_yield_multiplier',
                            label: tComparablesCategory('multiplierOptions.grossYieldMultiplier'),
                        },
                        {
                            value: 'net_yield_multiplier',
                            label: tComparablesCategory('multiplierOptions.netYieldMultiplier'),
                        },
                    ]}
                    setSelection={setSelectedMultiplierReference}
                    selection={selectedMultiplierReference}
                />
            )}
        </Stack>
    )
}
